import React, {useState, useEffect} from 'react';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import './Steg.css';

const CATEGORIES = [
    'Région de Tunis',
    'Région Nord',
    'Région Nord-Ouest',
    'Région Centre',
    'Région Sfax',
    'Région Sud-Ouest',
    'Région Sud',
];

const emptyForm = {
    region: '',
    numTel: '',
    numFixe: '',
    adresseMail: '',
    categorie: '',
};

const StegManagement = ({onNavigate, onLogout, currentPage}) => {
    const {hasPermission} = usePermissions();
    const [stegs, setStegs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState('success');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategorie, setFilterCategorie] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');

    // Auth config
    const getAuthConfig = () => {
        const token = localStorage.getItem('token');
        return {headers: {Authorization: `Bearer ${token}`}};
    };

    // Redirect if not admin/moderateur
    useEffect(() => {
        const userType = localStorage.getItem('typeUser');

        // Si c'est un modérateur, redirection systématique vers le profil frontend (demande utilisateur)
        if (userType === 'moderateur') {
            onNavigate('profile');
            return;
        }

        if (userType !== 'admin') {
            onNavigate('profile');
            return;
        }

        fetchStegs();
    }, [onNavigate]);

    const fetchStegs = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${import.meta.env.VITE_API_URL}/steg`, getAuthConfig());
            setStegs(Array.isArray(res.data) ? res.data : []);
        } catch (error) {
            console.error('Erreur chargement STEG:', error);
            if (error.response?.status === 401 || error.response?.status === 403) {
                onLogout();
            }
            setStegs([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const resetForm = () => {
        setFormData(emptyForm);
        setEditingItem(null);
    };

    const openModal = (item = null) => {
        setEditingItem(item);
        setFormData(item ? {
            region: item.region || '',
            numTel: item.numTel || '',
            numFixe: item.numFixe || '',
            adresseMail: item.adresseMail || '',
            categorie: item.categorie || '',
        } : emptyForm);
        setStatusMessage('');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        resetForm();
        setStatusMessage('');
    };

    const showStatus = (message, type = 'success') => {
        setStatusMessage(message);
        setStatusType(type);
        setTimeout(() => setStatusMessage(''), 4000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatusMessage('');
        try {
            if (editingItem) {
                await axios.put(
                    `${import.meta.env.VITE_API_URL}/steg/${editingItem._id}`,
                    formData,
                    getAuthConfig()
                );
                showStatus('Entrée STEG modifiée avec succès !');
            } else {
                await axios.post(
                    `${import.meta.env.VITE_API_URL}/steg`,
                    formData,
                    getAuthConfig()
                );
                showStatus('Entrée STEG créée avec succès !');
            }
            await fetchStegs();
            setTimeout(closeModal, 1200);
        } catch (error) {
            console.error('Erreur sauvegarde:', error);
            const msg = error.response?.data?.message || 'Erreur lors de la sauvegarde.';
            setStatusMessage(msg);
            setStatusType('error');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée STEG ?')) return;
        try {
            await axios.delete(`${import.meta.env.VITE_API_URL}/steg/${id}`, getAuthConfig());
            showStatus('Entrée STEG supprimée avec succès !');
            await fetchStegs();
        } catch (error) {
            console.error('Erreur suppression:', error);
            showStatus('Erreur lors de la suppression.', 'error');
        }
    };

    // Sort handler
    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    // Filter + sort
    const filtered = stegs
        .filter(s => {
            const q = searchQuery.toLowerCase();
            const matchSearch =
                !q ||
                s.region?.toLowerCase().includes(q) ||
                s.numTel?.toLowerCase().includes(q) ||
                s.numFixe?.toLowerCase().includes(q) ||
                s.categorie?.toLowerCase().includes(q);
            const matchCat = !filterCategorie || s.categorie === filterCategorie;
            return matchSearch && matchCat;
        })
        .sort((a, b) => {
            let aVal = a[sortField] ?? '';
            let bVal = b[sortField] ?? '';
            if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });

    const SortIcon = ({field}) => {
        if (sortField !== field) return <span className="sort-icon neutral">↕</span>;
        return <span className="sort-icon active">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
    };

    return (
        <div className="steg-management">
            <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />

            <div className="steg-content">
                {/* Header */}
                <div className="dashboard-header">
                    <div>
                        <h1>
                            <span className="steg-icon">⚡</span>
                            Gestion STEG
                        </h1>
                        <p>Gérer les contacts STEG par région et catégorie</p>
                    </div>
                    <div className="header-stats">
                        <div className="stat-badge">
                            <span className="stat-number">{stegs.length}</span>
                            <span className="stat-label">Total entrées</span>
                        </div>
                        <div className="stat-badge">
                            <span className="stat-number">{filtered.length}</span>
                            <span className="stat-label">Affichées</span>
                        </div>
                    </div>
                </div>

                {/* Status Message */}
                {statusMessage && !showModal && (
                    <div className={`status-message ${statusType}`}>
                        {statusMessage}
                    </div>
                )}

                {/* Toolbar */}
                <div className="steg-toolbar">
                    <div className="toolbar-left">
                        <div className="search-box">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                placeholder="Rechercher par région, téléphone..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <select
                            className="filter-select"
                            value={filterCategorie}
                            onChange={e => setFilterCategorie(e.target.value)}
                        >
                            <option value="">Toutes les catégories</option>
                            {CATEGORIES.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                    </div>
                    {hasPermission('steg', 'create') && (
                        <button className="btn-primary" onClick={() => openModal()}>
                            ＋ Nouvelle entrée STEG
                        </button>
                    )}
                </div>

                {/* Category cards summary */}
                <div className="category-summary">
                    {CATEGORIES.map(cat => {
                        const count = stegs.filter(s => s.categorie === cat).length;
                        return (
                            <div
                                key={cat}
                                className={`cat-card ${filterCategorie === cat ? 'active' : ''}`}
                                onClick={() => setFilterCategorie(filterCategorie === cat ? '' : cat)}
                            >
                                <span className="cat-count">{count}</span>
                                <span className="cat-name">{cat}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Table */}
                {isLoading ? (
                    <LoadingSpinner message="Chargement des entrées STEG..." />
                ) : (
                    <div className="steg-table-container">
                        <table className="steg-table">
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('region')} className="sortable">
                                        Région <SortIcon field="region" />
                                    </th>
                                    <th onClick={() => handleSort('numTel')} className="sortable">
                                        Num. Téléphone <SortIcon field="numTel" />
                                    </th>
                                    <th onClick={() => handleSort('numFixe')} className="sortable">
                                        Num. Fixe <SortIcon field="numFixe" />
                                    </th>
                                    <th onClick={() => handleSort('adresseMail')} className="sortable">
                                        Adresse Mail <SortIcon field="adresseMail" />
                                    </th>
                                    <th onClick={() => handleSort('categorie')} className="sortable">
                                        Catégorie <SortIcon field="categorie" />
                                    </th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="empty-state">
                                            <div className="empty-icon">⚡</div>
                                            <p>Aucune entrée STEG trouvée</p>
                                            <button className="btn-primary" onClick={() => openModal()}>
                                                Ajouter la première entrée
                                            </button>
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(steg => (
                                        <tr key={steg._id}>
                                            <td>
                                                <span className="region-name">{steg.region}</span>
                                            </td>
                                            <td>
                                                <a href={`tel:${steg.numTel}`} className="phone-link">
                                                    📱 {steg.numTel}
                                                </a>
                                            </td>
                                            <td>
                                                {steg.numFixe ? (
                                                    <a href={`tel:${steg.numFixe}`} className="phone-link">
                                                        ☎️ {steg.numFixe}
                                                    </a>
                                                ) : (
                                                    <span className="no-value">—</span>
                                                )}
                                            </td>
                                            <td>
                                                {steg.adresseMail ? (
                                                    <a href={`mailto:${steg.adresseMail}`} className="mail-link">
                                                        ✉️ {steg.adresseMail}
                                                    </a>
                                                ) : (
                                                    <span className="no-value">—</span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`categorie-badge cat-${CATEGORIES.indexOf(steg.categorie)}`}>
                                                    {steg.categorie}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                {hasPermission('steg', 'update') && (
                                                    <button
                                                        className="btn-edit"
                                                        onClick={() => openModal(steg)}
                                                        title="Modifier"
                                                    >
                                                        ✏️ Modifier
                                                    </button>
                                                )}
                                                {hasPermission('steg', 'delete') && (
                                                    <button
                                                        className="btn-delete"
                                                        onClick={() => handleDelete(steg._id)}
                                                        title="Supprimer"
                                                    >
                                                        🗑️ Supprimer
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal steg-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                {editingItem ? '✏️ Modifier l\'entrée STEG' : '➕ Nouvelle entrée STEG'}
                            </h2>
                            <button className="modal-close" onClick={closeModal}>×</button>
                        </div>

                        {statusMessage && (
                            <div className={`status-message ${statusType}`} style={{margin: '0 20px 10px'}}>
                                {statusMessage}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="modal-form">
                            {/* Région */}
                            <div className="form-group">
                                <label htmlFor="steg-region">Région *</label>
                                <input
                                    id="steg-region"
                                    type="text"
                                    name="region"
                                    value={formData.region}
                                    onChange={handleInputChange}
                                    placeholder="Ex: Ariana, Bizerte, Béja..."
                                    required
                                />
                            </div>

                            {/* Catégorie */}
                            <div className="form-group">
                                <label htmlFor="steg-categorie">Catégorie *</label>
                                <select
                                    id="steg-categorie"
                                    name="categorie"
                                    value={formData.categorie}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">— Sélectionner une catégorie —</option>
                                    {CATEGORIES.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Téléphones */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="steg-tel">Numéro Téléphone *</label>
                                    <input
                                        id="steg-tel"
                                        type="tel"
                                        name="numTel"
                                        value={formData.numTel}
                                        onChange={handleInputChange}
                                        placeholder="Ex: 71 123 456"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="steg-fixe">Numéro Fixe</label>
                                    <input
                                        id="steg-fixe"
                                        type="tel"
                                        name="numFixe"
                                        value={formData.numFixe}
                                        onChange={handleInputChange}
                                        placeholder="Ex: 71 234 567"
                                    />
                                </div>
                            </div>

                            {/* Adresse Mail */}
                            <div className="form-group">
                                <label htmlFor="steg-mail">Adresse Mail</label>
                                <input
                                    id="steg-mail"
                                    type="email"
                                    name="adresseMail"
                                    value={formData.adresseMail}
                                    onChange={handleInputChange}
                                    placeholder="Ex: contact.region@steg.com.tn"
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-secondary" onClick={closeModal}>
                                    Annuler
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingItem ? '💾 Modifier' : '➕ Créer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StegManagement;
